import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Package, AlertTriangle, Calendar, Coins, Users, Phone } from "lucide-react";

const peso = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatCard({ icon: Icon, label, value, sub, tone = "blue", testid }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    green: "bg-green-50 text-green-700",
  };
  return (
    <Card className="p-5 border-slate-200" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${tones[tone]}`}><Icon size={18} /></div>
        <div className="min-w-0">
          <div className="text-2xl font-semibold text-slate-900 truncate" style={{ fontFamily: "Work Sans" }}>{value}</div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
          {sub ? <div className="text-xs text-slate-600 mt-1">{sub}</div> : null}
        </div>
      </div>
    </Card>
  );
}

export default function Inventory() {
  const [summary, setSummary] = useState(null);
  const [expiring, setExpiring] = useState({ count: 0, total_value_at_risk: 0, items: [] });
  const [windowMonths, setWindowMonths] = useState(6);
  const [patientsModal, setPatientsModal] = useState(null);

  const load = async () => {
    const [s, e] = await Promise.all([
      api.get("/inventory/summary"),
      api.get(`/inventory/expiring?months=${windowMonths}`),
    ]);
    setSummary(s.data);
    setExpiring(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [windowMonths]);

  if (!summary) return <div className="p-8 text-slate-500">Loading...</div>;

  const lowStock = Array.isArray(summary.low_stock) ? summary.low_stock : [];
  const byCat = Array.isArray(summary.by_category) ? summary.by_category : [];
  const items = Array.isArray(expiring.items) ? expiring.items : [];

  return (
    <div className="p-6 md:p-8 max-w-[1500px]" data-testid="inventory-page">
      <div className="mb-6">
        <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>Inventory</h1>
        <p className="text-slate-500 text-sm mt-1">Stock value, expiring medicines, and patients who need them.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Coins} label="Total Inventory Value" value={peso(summary.total_value)} sub={`${summary.total_skus} SKUs · ${summary.total_items} units`} tone="blue" testid="stat-total-value" />
        <StatCard icon={AlertTriangle} label="Low Stock Items" value={summary.low_stock_count} sub="below threshold" tone="amber" testid="stat-low-stock" />
        <StatCard icon={Calendar} label={`Expiring within ${windowMonths} mo`} value={expiring.count} sub={`${peso(expiring.total_value_at_risk)} at risk`} tone="red" testid="stat-expiring" />
        <StatCard icon={Package} label="Active SKUs" value={summary.total_skus} sub={`${byCat.length} categories`} tone="green" testid="stat-skus" />
      </div>

      <Tabs defaultValue="expiring" className="space-y-4">
        <TabsList>
          <TabsTrigger value="expiring" data-testid="tab-expiring">Expiring Soon</TabsTrigger>
          <TabsTrigger value="lowstock" data-testid="tab-lowstock">Low Stock</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">By Category</TabsTrigger>
        </TabsList>

        <TabsContent value="expiring">
          <Card className="border-slate-200">
            <div className="p-4 flex items-center gap-3 border-b border-slate-200 flex-wrap">
              <span className="text-sm text-slate-700 font-medium">Window:</span>
              {[3, 6, 9, 12].map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant={windowMonths === m ? "default" : "outline"}
                  className={windowMonths === m ? "bg-blue-600 hover:bg-blue-700" : ""}
                  onClick={() => setWindowMonths(m)}
                  data-testid={`window-${m}`}
                >
                  {m} months
                </Button>
              ))}
              <span className="ml-auto text-sm text-slate-500">Total value at risk: <b className="text-red-700">{peso(expiring.total_value_at_risk)}</b></span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Medicine</th>
                    <th className="px-4 py-3 text-left font-semibold">Expiry</th>
                    <th className="px-4 py-3 text-right font-semibold">Stock</th>
                    <th className="px-4 py-3 text-right font-semibold">Value at Risk</th>
                    <th className="px-4 py-3 text-left font-semibold">Patients to Notify</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">No medicines expiring within {windowMonths} months. Nice!</td></tr>
                  ) : items.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`expiring-row-${m.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{m.medicine_name}</div>
                        <div className="text-xs text-slate-500">{m.brand_name}{m.strength ? ` · ${m.strength}` : ""}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`text-xs font-medium ${m.expired ? "text-red-700" : m.days_left <= 30 ? "text-red-600" : m.days_left <= 90 ? "text-amber-600" : "text-slate-700"}`}>
                          {m.expiry_date}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                          {m.expired ? "EXPIRED" : `${m.days_left} days`}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right mono text-xs">{m.stock_quantity}{m.unit ? ` ${m.unit}` : ""}</td>
                      <td className="px-4 py-3 text-right mono text-xs font-semibold text-red-700">{peso(m.value_at_risk)}</td>
                      <td className="px-4 py-3">
                        {(m.patients_needing || []).length === 0 ? (
                          <span className="text-xs text-slate-400">—</span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => setPatientsModal({ medicine: m.medicine_name, patients: m.patients_needing })} data-testid={`patients-btn-${m.id}`}>
                            <Users size={12} className="mr-1" /> {m.patients_needing.length} patient{m.patients_needing.length === 1 ? "" : "s"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="lowstock">
          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Medicine</th>
                    <th className="px-4 py-3 text-right font-semibold">Stock</th>
                    <th className="px-4 py-3 text-right font-semibold">Threshold</th>
                    <th className="px-4 py-3 text-right font-semibold">Unit Price</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {lowStock.length === 0 ? (
                    <tr><td colSpan="5" className="px-4 py-10 text-center text-slate-400">All medicines well stocked.</td></tr>
                  ) : lowStock.map((m) => (
                    <tr key={m.id} className="border-t border-slate-100" data-testid={`lowstock-row-${m.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{m.medicine_name}</td>
                      <td className="px-4 py-3 text-right mono text-xs text-red-700 font-semibold">{m.stock_quantity}{m.unit ? ` ${m.unit}` : ""}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{m.threshold}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{peso(m.unit_price)}</td>
                      <td className="px-4 py-3 text-right">
                        <Button asChild size="sm" variant="outline"><Link to="/medicines">Edit</Link></Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="categories">
          <Card className="border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs tracking-wider">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Category</th>
                    <th className="px-4 py-3 text-right font-semibold">Items</th>
                    <th className="px-4 py-3 text-right font-semibold">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {byCat.length === 0 ? (
                    <tr><td colSpan="3" className="px-4 py-10 text-center text-slate-400">No data yet.</td></tr>
                  ) : byCat.map((c) => (
                    <tr key={c.category} className="border-t border-slate-100" data-testid={`cat-row-${c.category}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{c.category}</td>
                      <td className="px-4 py-3 text-right mono text-xs">{c.items}</td>
                      <td className="px-4 py-3 text-right mono text-xs font-semibold">{peso(c.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!patientsModal} onOpenChange={(o) => !o && setPatientsModal(null)}>
        <DialogContent className="max-w-2xl" data-testid="patients-modal">
          <DialogHeader>
            <DialogTitle>Patients prescribed: {patientsModal?.medicine}</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-slate-200 max-h-96 overflow-auto">
            {(patientsModal?.patients || []).map((p) => (
              <div key={p.patient_id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="font-medium text-slate-900">{p.patient_name || "(unknown)"}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    {p.contact_number ? <span className="flex items-center gap-1"><Phone size={11} /> <span className="mono">{p.contact_number}</span></span> : null}
                    {p.last_prescribed ? <span>Last: {String(p.last_prescribed).slice(0, 10)}</span> : null}
                    {p.rx_number ? <span className="mono">{p.rx_number}</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
